import {useMemo} from 'react'
import productListInformationFragment from '@data/fragments/products/productListInformation'
import hasWebpSupport from '@helpers/misc/hasWebpSupport'
import useWebsiteId from '@hooks/useWebsiteId'
import {useApolloQuery} from '@packages/apollo-hooks'
import gql from 'graphql-tag'

import useLimit from './useLimit'
import useSetOutOfStockProducts from './useSetOutOfStockProducts'

export default function useProducts({categoryId, storeId, deliverAt, menuId, full}) {
  const limit = useLimit()
  const websiteId = useWebsiteId()
  const format = hasWebpSupport() ? 'webp' : 'jpeg'
  const {category, loading} = useApolloQuery({
    query: gql`
      query getCategoriesLayoutCategoryProducts_cached(
        $websiteId: ID
        $categoryId: ID
        $menuId: ID
        $format: String
        $limit: Float
        $highlighted: Boolean
      ) {
        category(websiteId: $websiteId, categoryId: $categoryId) {
          _id
          products(highlighted: $highlighted, limit: $limit, menuId: $menuId) {
            _id
            ...productListInformation
          }
        }
      }
      ${productListInformationFragment}
    `,
    variables: {
      websiteId,
      categoryId,
      menuId,
      format,
      limit: full ? null : limit,
      highlighted: !full
    },
    partial: true
  })

  const {outOfStockIds} = useSetOutOfStockProducts({websiteId, category, storeId, deliverAt})

  const memoizedProducts = useMemo(() => {
    if (loading) return []
    return (
      category?.products
        ?.filter(product => {
          if (!product.availabilityAt) return false
          if (product.availabilityAt.notAvailableMessage) return true
          return !!product.availabilityAt.available
        })
        .map(product => ({...product, isOutOfStock: outOfStockIds.includes(product._id)})) ?? []
    )
  }, [loading, category?.products, outOfStockIds])

  return {products: memoizedProducts, loading}
}
